import {
    brandingsURL,
    brandingURL,
    getBrandingConfigByCMEAndTypeURL,
    getBrandingIDSessionURL
} from '../../../NonReact/constants/ApiConstants'
import { BrandingConfigByTypeResponse } from '../Types/Types'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { CMEBrandingBackend, CMEBrandingBackendWrapper } from '../../CMEAddModify/Types'
import { APIData, JSONResponse } from '../../../NonReact/common/Types/Types'

export enum BrandingType {
    PREVIEW = 'preview',
    CURRENT = 'current',
    PREVIOUS = 'previous'
}

export type SSOButton = {
    Title: string
    Icon: string
    URL: string
}

export type BrandingWebLink = {
    URL: string
    Target: string
    Title: string
}

export type BrandingConfigImagery = {
    authBg?: string
    authBgType?: 'video' | 'image'
    mainLogo?: string
    mainLogoForWhiteBG?: string
    squareLogo?: string
    favicon?: string
    broadworksLogo?: string
}

export type BrandingConfigLanguages = {
    authSubtitle?: string
    authSubtitle2?: string
    broadworksName?: string
}

export type BrandingConfigLink = {
    title: string
    url: string
    target: string
}

export type BrandingConfigLinks = {
    mainSiteLink?: BrandingConfigLink
}

export type BrandingConfigSSOButton = {
    icon: string
    label: string
    url: string
}

export type BrandingConfigSSOButtons = BrandingConfigSSOButton[]

export type BrandingConfig = {
    imagery?: BrandingConfigImagery
    languages?: BrandingConfigLanguages
    ssoButtons?: BrandingConfigSSOButtons
    links?: BrandingConfigLinks
}

export type Branding = {
    BrandName?: string
    LogoURL: string
    PrimaryColour: string
    TransparentLogoURL: string
    BroadworksLogoURL: string
    BroadworksName: string
    URLMapping: string
    SSOButtons?: {
        [name: string]: SSOButton
    }
    MainLogoLink?: BrandingWebLink
    BrandingConfig?: BrandingConfig
    BrandingGUID?: string
}

export type BrandingListEntry = {
    name: string
    brandingID: number
}

export type BrandingSet = {
    brandingID: number
}

export type BrandingList = BrandingListEntry[]

/**
 * Gets the branding from the server.
 */
export const getBranding = (): Promise<JSONResponse<Branding>> =>
    requestGetJSON<JSONResponse<Branding>>(brandingURL, { cacheTime: Infinity })

export const getBrandings = (): Promise<JSONResponse<BrandingList>> =>
    requestGetJSON<JSONResponse<BrandingList>>(brandingsURL, { cacheTime: Infinity })

export const setBranding = (branding: BrandingListEntry): Promise<JSONResponse<void>> =>
    requestJSON<void, BrandingSet>(brandingURL, {
        method: RESTMethods.POST,
        body: branding
    })

export const getBrandingConfigByType = (cmeID: string, type: BrandingType): Promise<Nullable<CMEBrandingBackend>> =>
    requestGetJSON<APIData<CMEBrandingBackendWrapper>>(getBrandingConfigByCMEAndTypeURL(cmeID, type)).then(
        (response: Nullable<APIData<CMEBrandingBackendWrapper>>): Nullable<CMEBrandingBackend> =>
            response?.Data?.BrandingConfig
    )

export const deleteBrandingConfigByType = (cmeID: string, type: BrandingType): Promise<Nullable<void>> =>
    requestJSON(getBrandingConfigByCMEAndTypeURL(cmeID, type), { method: RESTMethods.DELETE })

/**
 * Adds branding by type.
 *
 * @param cmeID - The CME ID.
 * @param type - The branding type.
 * @param branding - The branding.
 */
export const addBrandingByType = (
    cmeID: string,
    type: BrandingType,
    branding: CMEBrandingBackend
): Promise<Nullable<string>> =>
    requestJSON<APIData<BrandingConfigByTypeResponse>, CMEBrandingBackend>(
        getBrandingConfigByCMEAndTypeURL(cmeID, type),
        {
            method: RESTMethods.POST,
            body: branding
        }
    ).then(
        (brandingConfigByTypeResponse: Nullable<APIData<BrandingConfigByTypeResponse>>): Nullable<string> =>
            brandingConfigByTypeResponse?.Data?.BrandingGUID
    )

export const applyBrandingIDToUserSession = (brandingID: string): Promise<Nullable<void>> =>
    requestJSON(getBrandingIDSessionURL(brandingID), { method: RESTMethods.PUT })

export const deleteBrandingIDFromUserSession = (): Promise<Nullable<void>> =>
    requestJSON(getBrandingIDSessionURL(), { method: RESTMethods.DELETE })
